import React, { useEffect, useState } from 'react';
import { Truck, HardHat, Tag, Check, Phone, Smartphone, Sparkles, HelpCircle, MapPin, CreditCard, Home } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useT } from '../utils/i18n';
const PROMO_CODE = 'FIRST10';
const PHONE_NUMBER = '(206) 619-3742';
const ROTATE_MS = 10000;
export function AnnouncementBar() {
  const location = useLocation();
  const t = useT();
  const isContractors = location.pathname.includes('subcontractors') || location.pathname.includes('contractors');
  const [copied, setCopied] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  // Auto-hide the phone number 3s after it slides out
  useEffect(() => {
    if (!phoneOpen) return;
    const id = setTimeout(() => setPhoneOpen(false), 3000);
    return () => clearTimeout(id);
  }, [phoneOpen]);
  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      try {
        sessionStorage.setItem('promo_hint', PROMO_CODE);
      } catch {}
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  // Rotating announcements. Pattern: promo → bulk → [order msg N], where
  // [order msg N] cycles through all 6 ordering taglines on each loop.
  // Sequence: 1-2-3, 1-2-4, 1-2-5, 1-2-6, 1-2-7, 1-2-8, then back to 1-2-3.
  const promoSlide = {
    key: 'promo',
    content: <>
        <span className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded-full bg-stone-900 text-amber-300 mr-2 shrink-0" data-id="element-137">
          <Tag className="w-3 h-3" data-id="element-138" />
        </span>
        <span className="hidden sm:inline text-[15px] font-semibold" data-id="element-139">
          {t('promo.firstOrder')}
        </span>
        <span className="sm:hidden text-[12px] font-semibold" data-id="element-140">
          {t('promo.firstOrderShort')}
        </span>
        <button onClick={handleCopy} className="inline-flex items-center gap-1.5 ml-2 bg-stone-900 hover:bg-stone-800 text-amber-300 px-2.5 py-0.5 rounded-md font-mono font-bold tracking-wider text-[11px] sm:text-xs transition-colors shrink-0" aria-label={`Copy promo code ${PROMO_CODE}`} data-id="element-141">
          {copied ? <>
              <Check className="w-3 h-3" data-id="element-142" />
              {t('promo.copied')}
            </> : <>
              {PROMO_CODE}
              <span className="text-amber-300/70 font-normal hidden sm:inline" data-id="element-143">
                {t('promo.tapToCopy')}
              </span>
            </>}
        </button>
      </>
  };
  const bulkSlide = {
    key: 'bulk',
    content: <>
        <span className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded-full bg-stone-900 text-amber-300 mr-2 shrink-0" data-id="element-144">
          <Truck className="w-3 h-3" data-id="element-145" />
        </span>
        <span className="hidden sm:inline text-[15px] font-semibold" data-id="element-146">
          {t('announce.bulkDelivery')}
        </span>
        <span className="sm:hidden text-[12px] font-semibold" data-id="element-147">
          {t('announce.bulkDeliveryShort')}
        </span>
      </>
  };
  const orderMessages = [{
    key: 'order-2',
    content: <>
          <span className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded-full bg-stone-900 text-amber-300 mr-2 shrink-0" data-id="element-148">
            <Smartphone className="w-3 h-3" data-id="element-149" />
          </span>
          <span className="hidden sm:inline text-[15px] font-semibold" data-id="element-150">
            Quote &amp; Order in Under a Minute
          </span>
          <span className="sm:hidden text-[12px] font-semibold" data-id="element-151">
            Quote &amp; Order in under 60 seconds
          </span>
        </>
  }, {
    key: 'order-5',
    content: <>
          <span className="hidden sm:inline-flex items-center justify-center w-6 h-6 rounded-full bg-stone-900 text-amber-300 mr-2 shrink-0" data-id="element-152">
            <Smartphone className="w-3.5 h-3.5" data-id="element-153" />
          </span>
          <span className="text-[14px] sm:text-[18px] font-bold tracking-tight" data-id="element-154">
            Order Anywhere, Anytime
          </span>
        </>
  }, {
    key: 'order-6',
    content: <>
          <span className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded-full bg-stone-900 text-amber-300 mr-2 shrink-0" data-id="element-155">
            <CreditCard className="w-3 h-3" data-id="element-156" />
          </span>
          <span className="hidden sm:inline text-[15px] font-semibold" data-id="element-157">
            Pay on Delivery — No Card Needed
          </span>
          <span className="sm:hidden text-[12px] font-semibold" data-id="element-158">
            Pay on Delivery, No Card Needed
          </span>
        </>
  }, {
    key: 'order-7',
    content: <>
          <span className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded-full bg-stone-900 text-amber-300 mr-2 shrink-0" data-id="element-159">
            <Home className="w-3 h-3" data-id="element-160" />
          </span>
          <span className="hidden sm:inline text-[15px] font-semibold" data-id="element-161">
            Skip the Big Box — Order from Locals
          </span>
          <span className="sm:hidden text-[12px] font-semibold" data-id="element-162">
            Skip the Big Box — Local Delivery
          </span>
        </>
  }];
  // Promo alternates with every other slide. Other slides = bulk + ordering messages.
  // Pattern: promo → bulk → promo → order-1 → promo → order-2 → ...
  const otherSlides = [bulkSlide, ...orderMessages];
  const TOTAL_TICKS = 2 * otherSlides.length;
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setIdx(p => (p + 1) % TOTAL_TICKS);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [TOTAL_TICKS]);
  const isPromoSlot = idx % 2 === 0;
  const otherIdx = Math.floor(idx / 2) % otherSlides.length;
  const currentSlide = isPromoSlot ? promoSlide : otherSlides[otherIdx];
  return <div className="bg-gradient-to-r from-yellow-300 via-yellow-200 to-yellow-300 dark:from-amber-400 dark:via-amber-300 dark:to-amber-400 text-stone-900 py-1.5 px-4 text-xs sm:text-sm font-medium relative z-[60] border-b border-yellow-400/50 dark:border-amber-500/40" data-id="element-163">
      {/*
                                      Mobile: justify-between keeps the right-side items pinned to the
                                      right edge so they don't shift when the rotating message changes
                                      length. Desktop: centered.
                                    */}
      <div className="max-w-7xl mx-auto flex items-center justify-between md:justify-center gap-3 sm:gap-4" data-id="element-164">
        {/* Rotating left slot */}
        <div className="flex-1 md:flex-none min-w-0 flex items-center min-h-[20px] relative" data-id="element-165">
          <AnimatePresence mode="wait" data-id="element-166">
            <motion.div key={currentSlide.key} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="flex items-center whitespace-nowrap min-w-0 max-w-full" data-id="element-167">
              {currentSlide.content}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Right group — stays static */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0" data-id="element-168">
          <span className="opacity-40" data-id="element-169">|</span>

          {/* Phone — icon only; number slides out on hover/tap, hides after 3s */}
          <div className="relative flex items-center" onMouseEnter={() => setPhoneOpen(true)} onClick={() => setPhoneOpen(true)} data-id="element-170">
            <a href="tel:206-619-3742" aria-label={`Call ${PHONE_NUMBER}`} className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-stone-900/10 transition-colors" data-id="element-171">
              <Phone className="w-4 h-4" data-id="element-172" />
            </a>
            <AnimatePresence data-id="element-173">
              {phoneOpen && <motion.span initial={{
              opacity: 0,
              x: -8,
              width: 0
            }} animate={{
              opacity: 1,
              x: 0,
              width: 'auto'
            }} exit={{
              opacity: 0,
              x: -8,
              width: 0
            }} transition={{
              duration: 0.28,
              ease: [0.22, 1, 0.36, 1]
            }} className="ml-1.5 text-stone-900 font-semibold tracking-tight text-xs sm:text-sm whitespace-nowrap pointer-events-none overflow-hidden" data-id="element-174">
                  {PHONE_NUMBER}
                </motion.span>}
            </AnimatePresence>
          </div>

          <span className="opacity-40" data-id="element-175">|</span>

          <Link to="/subcontractors" className={`flex items-center transition-colors font-[600] ${isContractors ? 'text-emerald-700 hover:text-emerald-800' : 'text-stone-900 hover:text-stone-700'}`} data-id="element-176">
            <HardHat className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" data-id="element-177" />
            {t('announce.contractors')}
          </Link>
        </div>
      </div>
    </div>;
}